import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";

export const RSVPForm = ({ colors, API, attendLabel = "Attending", declineLabel = "Not Attending", invitee, eventKey = "main" }) => {
  const [form, setForm] = useState({ name: "", men: 1, women: 0, attending: true, message: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (invitee && invitee.guestName) {
      setForm((f) => (f.name ? f : { ...f, name: invitee.guestName }));
    }
  }, [invitee]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    const men = Number(form.men) || 0;
    const women = Number(form.women) || 0;
    const guests = Math.max(men + women, form.attending ? 1 : 0);
    setSending(true);
    try {
      await axios.post(`${API}/rsvp`, {
        name: form.name,
        message: form.message,
        attending: form.attending,
        men,
        women,
        guests,
        guestSlug: invitee?.slug || "",
        invitedName: invitee?.guestName || "",
        eventKey: eventKey || "main",
      });
      setDone(true);
      toast.success("Thank you! Your response is recorded.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    border: `1px solid ${colors.primary}33`,
    background: "rgba(255,255,255,0.6)",
    color: colors.text,
  };

  if (done)
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-8"
        data-testid="rsvp-success"
      >
        <p className="font-display" style={{ fontSize: 30, color: colors.text }}>
          Jazak'Allah Khair
        </p>
        <p className="font-body text-sm mt-2" style={{ color: colors.secondary }}>
          We look forward to celebrating with you.
        </p>
      </motion.div>
    );

  return (
    <form onSubmit={submit} className="w-full max-w-sm mx-auto flex flex-col gap-4" data-testid="rsvp-form">
      <input
        data-testid="rsvp-name-input"
        placeholder="Your Name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="rounded-lg px-4 py-3 font-body text-sm outline-none focus:ring-2"
        style={inputStyle}
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-body tracking-wide" style={{ color: colors.secondary }}>Men</span>
          <input
            data-testid="rsvp-men-input"
            type="number"
            min="0"
            placeholder="0"
            value={form.men}
            onChange={(e) => setForm({ ...form, men: e.target.value })}
            className="rounded-lg px-4 py-3 font-body text-sm outline-none"
            style={inputStyle}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-body tracking-wide" style={{ color: colors.secondary }}>Women</span>
          <input
            data-testid="rsvp-women-input"
            type="number"
            min="0"
            placeholder="0"
            value={form.women}
            onChange={(e) => setForm({ ...form, women: e.target.value })}
            className="rounded-lg px-4 py-3 font-body text-sm outline-none"
            style={inputStyle}
          />
        </div>
      </div>
      <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${colors.primary}33` }}>
        {[
          { label: attendLabel, val: true },
          { label: declineLabel, val: false },
        ].map((o) => (
          <button
            key={o.label}
            type="button"
            data-testid={`rsvp-attending-${o.val}`}
            onClick={() => setForm({ ...form, attending: o.val })}
            className="flex-1 text-xs font-body tracking-wide py-2.5 transition-colors"
            style={{
              background: form.attending === o.val ? colors.primary : "transparent",
              color: form.attending === o.val ? "#fff" : colors.text,
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
      <textarea
        data-testid="rsvp-message-input"
        placeholder="Message / Blessings (optional)"
        value={form.message}
        rows={3}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        className="rounded-lg px-4 py-3 font-body text-sm outline-none resize-none"
        style={inputStyle}
      />
      <motion.button
        data-testid="rsvp-submit-btn"
        type="submit"
        disabled={sending}
        whileTap={{ scale: 0.97 }}
        className="rounded-full py-3 font-body text-sm tracking-widest"
        style={{ background: colors.primary, color: "#fff" }}
      >
        {sending ? "SENDING..." : "SEND RSVP"}
      </motion.button>
    </form>
  );
};
